/**
 * PipelineService — v3 port of the SPFx PipelineService.
 *
 * Source (read-only):
 *   C:/Projects/retail-account-opening/spfx-account-opening-v2/.../services/PipelineService.ts
 *
 * Port policy:
 *   The SPFx PipelineService is 61k lines of browser-side jXRL orchestration
 *   (CustAdd, CustMod, AcctIdGen, AcctAdd, …) that v3 MOVED to the Function
 *   (PCBAccountOpeningApi). The client no longer executes Core-Director
 *   calls directly; instead the Function runs the pipeline and persists
 *   `PipelineSnapshot` into `Carts.pipelineSnapshotJson`. So the v3
 *   PipelineService is smaller than SPFx by design — we keep:
 *
 *     1. The 9-step registry (`RAO_PIPELINE_STEPS`) — source of truth for
 *        labels, descriptions, and ordering rendered by PipelineProgress.
 *     2. Step-progression helpers (`canAdvance`, `next`, `markRunning`,
 *        `markComplete`, `markFailed`) — pure functions over `PipelineSnapshot`.
 *     3. Validation gates (what must be true about a `CartDto` before the
 *        Function will accept `POST /v3/carts/{id}/commit`). These mirror
 *        the SPFx gate checks (people/products/ownership/compliance).
 *     4. A thin `resume()` helper that asks the Function to pick a paused
 *        cart back up (post-sign webhook or manual "Continue"). The Function
 *        keeps the authoritative replay state; we just kick the door.
 *
 * Anything that was SPFx-side Core-Director invocation (CustAdd, AcctAdd, …)
 * is intentionally NOT ported — those calls live in the Function now. Keeping
 * them client-side would re-duplicate the production pipeline and risk
 * double-writes (per v3-architecture-spec §4).
 */

import type { ApiClient } from '@retail-ops/api-client';
import {
  asIso8601,
  type CartDto,
  type PipelineSnapshot,
  type PipelineStepExecution,
  type SigningSessionStatusResponse,
} from '@retail-ops/shared-types';
import type { PipelineStepStatus } from '@retail-ops/shared-types/enums';
import { RAO_PIPELINE_STEPS, type RaoPipelineStepDef } from '@/modules/RAO/constants';

// ─── Step registry ──────────────────────────────────────────────────────────

/** Re-exported from constants.ts so callers have a single import path. */
export const PIPELINE_STEPS: ReadonlyArray<RaoPipelineStepDef> = RAO_PIPELINE_STEPS;

export function stepDef(stepCode: string): RaoPipelineStepDef | undefined {
  return PIPELINE_STEPS.find((s) => s.stepCode === stepCode);
}

export function stepIndex(stepCode: string): number {
  return PIPELINE_STEPS.findIndex((s) => s.stepCode === stepCode);
}

// ─── Snapshot helpers ────────────────────────────────────────────────────────

/**
 * Build an empty snapshot sized to the 9-step registry. Mirrors the
 * `generatePipelineSteps` SPFx helper but for the v3 fixed-sequence pipeline
 * (the dynamic CustAdd-per-person fanout happens server-side).
 */
export function emptySnapshot(pipelineName = 'rao-v3'): PipelineSnapshot {
  const now = asIso8601(new Date().toISOString());
  return {
    pipelineName,
    version: 1,
    currentStep: PIPELINE_STEPS[0]!.stepCode,
    currentIndex: 0,
    totalSteps: PIPELINE_STEPS.length,
    startedAt: now,
    lastAdvancedAt: now,
    steps: PIPELINE_STEPS.map((def, idx) => ({
      stepCode: def.stepCode,
      stepOrder: idx,
      status: 'pending' as PipelineStepStatus,
    })),
  };
}

/** True if snapshot has a terminal step (complete for all non-AwaitSignatures). */
export function isComplete(snapshot: PipelineSnapshot | undefined): boolean {
  if (!snapshot) return false;
  return snapshot.steps.every((s) => s.status === 'complete' || s.status === 'skipped');
}

export function currentStep(snapshot: PipelineSnapshot | undefined): PipelineStepExecution | undefined {
  if (!snapshot) return undefined;
  return snapshot.steps.find((s) => s.stepCode === snapshot.currentStep);
}

export function failedStep(snapshot: PipelineSnapshot | undefined): PipelineStepExecution | undefined {
  if (!snapshot) return undefined;
  return snapshot.steps.find((s) => s.status === 'failed');
}

/** The next `pending` step after the current index, if any. */
export function nextStep(snapshot: PipelineSnapshot): PipelineStepExecution | undefined {
  const idx = snapshot.currentIndex;
  for (let i = idx + 1; i < snapshot.steps.length; i++) {
    const s = snapshot.steps[i];
    if (s && s.status === 'pending') return s;
  }
  return undefined;
}

export function canAdvance(snapshot: PipelineSnapshot): boolean {
  const cur = currentStep(snapshot);
  if (!cur) return false;
  return cur.status === 'complete' || cur.status === 'skipped';
}

// ─── Mutation helpers (pure — return new snapshot) ──────────────────────────

type MutPatch = Partial<PipelineStepExecution> & { status: PipelineStepStatus };

function patchStep(snapshot: PipelineSnapshot, stepCode: string, patch: MutPatch): PipelineSnapshot {
  const nextSteps = snapshot.steps.map((s) => (s.stepCode === stepCode ? { ...s, ...patch } : s));
  // If we're completing the current step and there's a next pending one, advance.
  let { currentStep: currentCode, currentIndex } = snapshot;
  if (patch.status === 'complete' && currentCode === stepCode) {
    const nxtIdx = nextSteps.findIndex((s, i) => i > currentIndex && s.status === 'pending');
    if (nxtIdx >= 0) {
      currentCode = nextSteps[nxtIdx]!.stepCode;
      currentIndex = nxtIdx;
    }
  }
  return {
    ...snapshot,
    steps: nextSteps,
    currentStep: currentCode,
    currentIndex,
    lastAdvancedAt: asIso8601(new Date().toISOString()),
  };
}

export function markRunning(snapshot: PipelineSnapshot, stepCode: string): PipelineSnapshot {
  return patchStep(snapshot, stepCode, {
    status: 'running',
    startedAt: snapshot.steps.find((s) => s.stepCode === stepCode)?.startedAt ?? asIso8601(new Date().toISOString()),
  });
}

export function markComplete(snapshot: PipelineSnapshot, stepCode: string, result?: unknown): PipelineSnapshot {
  const prev = snapshot.steps.find((s) => s.stepCode === stepCode);
  const endedAt = asIso8601(new Date().toISOString());
  const elapsedMs = prev?.startedAt ? Date.now() - Date.parse(prev.startedAt) : undefined;
  return patchStep(snapshot, stepCode, {
    status: 'complete',
    endedAt,
    ...(elapsedMs !== undefined ? { elapsedMs } : {}),
    ...(result !== undefined ? { result } : {}),
  });
}

export function markFailed(
  snapshot: PipelineSnapshot,
  stepCode: string,
  error: string,
  httpStatus?: number,
): PipelineSnapshot {
  const prev = snapshot.steps.find((s) => s.stepCode === stepCode);
  return patchStep(snapshot, stepCode, {
    status: 'failed',
    endedAt: asIso8601(new Date().toISOString()),
    lastError: error,
    attempts: (prev?.attempts ?? 0) + 1,
    ...(httpStatus !== undefined ? { httpStatus } : {}),
  });
}

export function markSkipped(snapshot: PipelineSnapshot, stepCode: string, reason?: string): PipelineSnapshot {
  return patchStep(snapshot, stepCode, {
    status: 'skipped',
    endedAt: asIso8601(new Date().toISOString()),
    ...(reason ? { lastError: reason } : {}),
  });
}

// ─── Commit gates ───────────────────────────────────────────────────────────

/**
 * What the Function's /v3/carts/{id}/commit endpoint will enforce — we
 * pre-flight these client-side so we never show a red server error for
 * something the officer could fix in-wizard.
 *
 * Mirrors the SPFx PipelineService.readyForSubmit checks:
 *   - at least one person in role 'primary'
 *   - at least one product
 *   - every product has an owner assignment
 *   - cip/ofac is 'clear' for every signer
 */
export interface PipelineGateFailure {
  code: 'NO_PRIMARY' | 'NO_PRODUCTS' | 'UNASSIGNED_PRODUCT' | 'CIP_NOT_CLEAR' | 'MISSING_OFFICER' | 'MISSING_BRANCH';
  message: string;
  detail?: Record<string, unknown>;
}

export interface PipelineGateReport {
  ready: boolean;
  failures: ReadonlyArray<PipelineGateFailure>;
}

/**
 * Inspects the CartDto alone (the stringified JSON envelopes are parsed loosely).
 * Callers may wish to consult peopleJson through the RAO `parsePeople` helper
 * — but to keep PipelineService free of UI-module imports we re-parse inline.
 */
export function validateForCommit(cart: CartDto): PipelineGateReport {
  const failures: PipelineGateFailure[] = [];

  if (!cart.officerCode) {
    failures.push({ code: 'MISSING_OFFICER', message: 'Officer code is required before commit.' });
  }
  if (!cart.branch) {
    failures.push({ code: 'MISSING_BRANCH', message: 'Branch is required before commit.' });
  }

  // People gate
  const people = safeParseArray(cart.peopleJson, 'people');
  const hasPrimary = people.some((p) => String(p['role']).toLowerCase() === 'primary');
  if (!hasPrimary) {
    failures.push({ code: 'NO_PRIMARY', message: 'Cart must have a primary applicant.' });
  }

  const cipBad = people.filter((p) => p['cipStatus'] && p['cipStatus'] !== 'clear');
  if (cipBad.length > 0) {
    failures.push({
      code: 'CIP_NOT_CLEAR',
      message: `CIP/OFAC not cleared for ${cipBad.length} applicant(s).`,
      detail: { count: cipBad.length },
    });
  }

  // Products gate
  const products = safeParseArray(cart.productsJson, 'products');
  if (products.length === 0) {
    failures.push({ code: 'NO_PRODUCTS', message: 'Cart must have at least one product.' });
  }

  const unassigned = products.filter((pr) => !pr['primaryOwnerId']);
  if (unassigned.length > 0) {
    failures.push({
      code: 'UNASSIGNED_PRODUCT',
      message: `${unassigned.length} product(s) have no primary owner.`,
      detail: { count: unassigned.length },
    });
  }

  return { ready: failures.length === 0, failures };
}

function safeParseArray(json: string | undefined, key?: string): Array<Record<string, unknown>> {
  if (!json) return [];
  try {
    const raw = JSON.parse(json) as unknown;
    if (Array.isArray(raw)) return raw as Array<Record<string, unknown>>;
    if (key && raw && typeof raw === 'object' && Array.isArray((raw as Record<string, unknown>)[key])) {
      return (raw as Record<string, unknown>)[key] as Array<Record<string, unknown>>;
    }
    return [];
  } catch {
    return [];
  }
}

// ─── PipelineService class — convenience wrapper over the helpers ───────────

/**
 * Plain class so the CartProvider can memoize one instance per ApiClient.
 * Methods are delegates to the pure helpers above; having them on a class
 * lets the CartContext expose `pipeline.canAdvance(snapshot)` etc. without
 * the consumer importing the helpers directly.
 */
export class PipelineService {
  public readonly steps = PIPELINE_STEPS;

  public emptySnapshot(): PipelineSnapshot {
    return emptySnapshot();
  }

  public stepDef(stepCode: string): RaoPipelineStepDef | undefined {
    return stepDef(stepCode);
  }

  public currentStep(snapshot: PipelineSnapshot | undefined): PipelineStepExecution | undefined {
    return currentStep(snapshot);
  }

  public nextStep(snapshot: PipelineSnapshot): PipelineStepExecution | undefined {
    return nextStep(snapshot);
  }

  public canAdvance(snapshot: PipelineSnapshot): boolean {
    return canAdvance(snapshot);
  }

  public isComplete(snapshot: PipelineSnapshot | undefined): boolean {
    return isComplete(snapshot);
  }

  public failedStep(snapshot: PipelineSnapshot | undefined): PipelineStepExecution | undefined {
    return failedStep(snapshot);
  }

  public markRunning(snapshot: PipelineSnapshot, stepCode: string): PipelineSnapshot {
    return markRunning(snapshot, stepCode);
  }

  public markComplete(snapshot: PipelineSnapshot, stepCode: string, result?: unknown): PipelineSnapshot {
    return markComplete(snapshot, stepCode, result);
  }

  public markFailed(snapshot: PipelineSnapshot, stepCode: string, error: string, httpStatus?: number): PipelineSnapshot {
    return markFailed(snapshot, stepCode, error, httpStatus);
  }

  public markSkipped(snapshot: PipelineSnapshot, stepCode: string, reason?: string): PipelineSnapshot {
    return markSkipped(snapshot, stepCode, reason);
  }

  public validateForCommit(cart: CartDto): PipelineGateReport {
    return validateForCommit(cart);
  }
}

// -----------------------------------------------------------------------------
// Webhook-resumption bridge (RSC port — see modules/RSC/hooks/useSigningSession.ts)
// -----------------------------------------------------------------------------
//
// Legacy SPFx halted the pipeline at the `AwaitSignatures` step and resumed
// via `PipelineContext.resumePostSign(currentSessionMeta)` once the UI saw all
// signers complete (ReviewScreen.tsx:518). In v3 the server owns the
// authoritative replay; these helpers are the CLIENT-side notification the
// RSC hook invokes when it observes a webhook relay.
//
// Both are intentionally idempotent + best-effort. They never `throw` into the
// render tree — failures are logged and the poll continues.

/**
 * Called by `useSigningSession.onCompleted` when the envelope has reached
 * all-signed / completed. Asks the Function to pick up the paused cart.
 *
 * If the Function has no paused pipeline for the cart, the `/v3/carts/{id}/resume`
 * endpoint should return a benign `{ resumed: false }` — we log and move on.
 */
export async function onEnvelopeCompleted(
  apiClient: ApiClient,
  params: {
    sessionId: string;
    cartId?: string;
    status: SigningSessionStatusResponse;
  },
): Promise<void> {
  if (!params.cartId) return;
  try {
    await apiClient.http.post<unknown>(
      `/v3/carts/${encodeURIComponent(params.cartId)}/resume`,
      {
        body: {
          trigger: 'signing-envelope-complete',
          sessionId: params.sessionId,
          envelopeStatus: params.status.Status,
        },
      },
    );
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[PipelineService] onEnvelopeCompleted resume failed:', err);
  }
}

/**
 * Called by `useSigningSession.onCorrected` when the webhook relay surfaced a
 * `signing.envelope.corrected` event. Emits a lightweight session event so the
 * audit trail reflects the dedup point; the Function's SignNow correction
 * handler is already authoritative for envelope mutation, so we do NOT call
 * the pipeline resume here.
 */
export async function onCorrectionCompleted(
  apiClient: ApiClient,
  params: { sessionId: string; eventId: string; payload?: Record<string, unknown> },
): Promise<void> {
  try {
    await apiClient.retail.events.write({
      eventType: 'signing-correction-observed',
      eventCategory: 'signing',
      sessionId: params.sessionId,
      payloadJson: JSON.stringify({ sourceEventId: params.eventId, ...(params.payload ?? {}) }),
    });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[PipelineService] onCorrectionCompleted event-write failed:', err);
  }
}
